import {
  Alert,
  Button,
  Group,
  Modal,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  ForgejoRequestError,
  HTTP_CONFLICT,
  HTTP_UNPROCESSABLE_ENTITY,
  HttpError,
} from "./api";
import { FORGEJO_INVALID_BOT_USERNAME_DETAIL } from "./forgejoErrors";
import {
  bindBotAccount,
  type BindBotAccountRequest,
  type BotAccountSummary,
} from "./tokensApi";

// Предел длины логина — `max_length` поля `username` в схеме
// bot-account и правило имени Forgejo (`username.py`).
const MAX_BOT_USERNAME_LENGTH = 255;

export interface BindBotAccountModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly role: string;
  readonly onBound: () => void;
}

interface BindBotAccountBodyProps {
  readonly role: string;
  readonly onClose: () => void;
  readonly onBound: () => void;
}

// Тело формы — монтируется только когда модалка открыта (сброс state
// между открытиями, тот же приём что в TokenUpdateModal/ForgetBotModal).
function BindBotAccountModalBody(props: BindBotAccountBodyProps): ReactElement {
  const { t } = useTranslation();
  const { role, onClose, onBound } = props;
  const queryClient = useQueryClient();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);

  // Отказ 422 с `detail` — логин не по правилам Forgejo: текст идёт на
  // поле логина, а не в Alert формы.
  const isInvalidUsername = (err: Error): boolean =>
    err instanceof ForgejoRequestError &&
    err.status === HTTP_UNPROCESSABLE_ENTITY &&
    err.detail === FORGEJO_INVALID_BOT_USERNAME_DETAIL;

  // Маппинг доменных HTTP-кодов на user-facing сообщения. 422 (кроме
  // логина не по форме, см. выше) — невалидные креды бота / хост
  // недоступен; 409 — логин занят
  // tombstone'ом забытого бота; прочее — generic.
  // ``err`` типизирован как ``Error`` (тип ошибки мутации) — fallback на
  // не-Error не нужен, поэтому берём ``err.message`` напрямую.
  const errorMessage = (err: Error): string => {
    if (err instanceof HttpError) {
      if (err.status === HTTP_UNPROCESSABLE_ENTITY) {
        return t("connections.tokens.bindBot.errorInvalidCreds");
      }
      if (err.status === HTTP_CONFLICT) {
        return t("connections.tokens.bindBot.errorTombstone");
      }
    }
    return t("connections.genericError", { message: err.message });
  };

  const mutation = useMutation<BotAccountSummary, Error, BindBotAccountRequest>(
    {
      mutationFn: (body) => bindBotAccount(body),
      onSuccess: () => {
        setFormError(null);
        setUsernameError(null);
        // Привязка делает can_issue=true → инвалидируем can-issue роли
        // (тот же queryKey, что в AddTokenFlow) для авто-refetch.
        void queryClient.invalidateQueries({
          queryKey: ["connections", "forgejo", "can-issue", role],
        });
        onBound();
        onClose();
      },
      onError: (err) => {
        if (isInvalidUsername(err)) {
          setFormError(null);
          setUsernameError(
            t("connections.tokens.bindBot.errorInvalidUsername"),
          );
          return;
        }
        setUsernameError(null);
        setFormError(errorMessage(err));
      },
    },
  );

  // Логин уходит без внешних пробелов: в имени Forgejo их не бывает.
  const handleSubmit = (): void => {
    setUsernameError(null);
    const login = username.trim();
    if (login === "" || password === "") {
      setFormError(t("connections.tokens.bindBot.errorRequired"));
      return;
    }
    // Длинный логин отбивает схема запроса списком ошибок, без `detail`
    // логина, поэтому предел проверяется здесь.
    if (login.length > MAX_BOT_USERNAME_LENGTH) {
      setFormError(null);
      setUsernameError(t("connections.tokens.bindBot.errorInvalidUsername"));
      return;
    }
    setFormError(null);
    mutation.mutate({ username: login, password });
  };

  return (
    <Stack gap="sm">
      <Text size="sm">{t("connections.tokens.bindBot.description")}</Text>

      {formError !== null ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {formError}
        </Alert>
      ) : null}

      <TextInput
        label={t("connections.tokens.bindBot.usernameLabel")}
        placeholder={t("connections.tokens.bindBot.usernamePlaceholder")}
        value={username}
        error={usernameError}
        onChange={(e) => {
          setUsername(e.currentTarget.value);
          setUsernameError(null);
        }}
        required
      />

      <PasswordInput
        label={t("connections.tokens.bindBot.passwordLabel")}
        placeholder={t("connections.tokens.bindBot.passwordPlaceholder")}
        value={password}
        onChange={(e) => {
          setPassword(e.currentTarget.value);
        }}
        required
      />

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t("connections.modalCancel")}
        </Button>
        <Button onClick={handleSubmit} loading={mutation.isPending}>
          {t("connections.tokens.bindBot.submitButton")}
        </Button>
      </Group>
    </Stack>
  );
}

// Модалка POST /bot-account — привязка существующего бота по Basic Auth,
// чтобы включить авто-выпуск per-role токенов без site-admin.
export function BindBotAccountModal(
  props: BindBotAccountModalProps,
): ReactElement {
  const { t } = useTranslation();
  const { opened, onClose, role, onBound } = props;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("connections.tokens.bindBot.title")}
      size="md"
    >
      {opened ? (
        <BindBotAccountModalBody
          role={role}
          onClose={onClose}
          onBound={onBound}
        />
      ) : null}
    </Modal>
  );
}
